import { call, fork, put, select, takeLatest } from 'redux-saga/effects';
import { initialize } from 'redux-form';
import { delay } from 'redux-saga';
import moment from 'moment';

import {
  REPORTS_TRANSACTIONS_PAGE_REQUEST,
  REPORTS_TRANSACTIONS_ON_SEARCH,
  REPORTS_TRANSACTIONS_ON_REACH_BOTTOM,
  REPORTS_TRANSACTIONS_GET_ORDER_DETAIL,
  REPORTS_TRANSACTIONS_ISSUE_REFUND_REQUEST,
  REPORTS_TRANSACTIONS_CONFIRM_SET_QUANTITY,
} from '../../actions/reports/transactionsv2/constants';
import {
  hideRefundBreakdownModal,
  hideTransactionDetail,
  showTransactionDetailModal,
  hideSetQuantityModal,
  hideTransactionDetailModal,
} from '../../actions/reports/transactions';
import {
  setOrderSummary,
  setOrdersByDate,
  setNextCursor,
  setHasMore,
  setOutletById,
  setPaymentMethods,
  setOrderDetail,
  setRefundItems,
  setRefundItemsPromo,
  pageRequest,
} from '../../actions/reports/transactionsv2';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';
import * as actNotif from '../../actions/notifications';
import * as actActionBar from '../../actions/actionBar';
import * as action from '../../actions';

import { outlets as getOutlets } from '../../api/outlets';
import * as apiOrderReporting from '../../api/orderReporting';
import * as apiSales from '../../api/sales';
import { getOrderDetail } from '../../api/openApi/order-reporting';
import { DEFAULT_START_HOUR, DEFAULT_END_HOUR } from '../../utils/hourFilter';
import {
  getSelectedOutletIdFromLocalStorage,
  setSelectedOutletIdToLocalStorage,
} from '../../utils/selectedOutletId';
import { sortOrderItems } from '../../utils/order';
import { assertErrorHasMessage } from '../../utils/assert';
import { getFilteredDateAsMoment } from '../api/get-filtered-date';
import { postRefundV2 } from '../api/refunds';
import { getAllPaymentMethods } from './transactions';
import {
  constructRefundItemsPromo,
  extractForRefundBreakdown,
} from './helpers';

function formatDate(
  { startDate, endDate, startHour, endHour, allDay },
  dateFormat
) {
  let startTime = `${moment(startDate).format(`${dateFormat}00:00:00`)}`;
  let endTime = `${moment(endDate).format(`${dateFormat}23:59:59`)}`;

  if (!allDay) {
    // eslint-disable-next-line no-param-reassign
    startHour = startHour || '00:00';
    // eslint-disable-next-line no-param-reassign
    endHour = endHour || '23:59';

    startTime = `${moment(startDate).format(`${dateFormat}${startHour}:00`)}`;
    endTime = `${moment(endDate).format(`${dateFormat}${endHour}:00`)}`;
  }

  return { startTime, endTime };
}

function* getQueryParam(searchValues) {
  const { date, keyword } = searchValues;

  const nextCursor = yield select(
    (state: any) => state.reports.transactionsv2.nextCursor
  );

  const dateFormat = 'YYYY-MM-DDT';
  const queryDates = formatDate(date, dateFormat);

  const queryParam = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    start_time: queryDates.startTime,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    end_time: queryDates.endTime,
    keyword,
    cursor: nextCursor,
  };
  return queryParam;
}

function getHeaders(searchValues) {
  const { outletIds } = searchValues;
  const headers = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    outlet_id: outletIds.length > 1 ? '' : outletIds[0],
  };
  return headers;
}

function formatDateForDailySummaries(
  { startDate, endDate, startHour, endHour, allDay },
  queryDates
) {
  const dateFormat = 'YYYY-MM-DD';
  const defaultStartTime = '00:00';
  const defaultEndTime = '23:59';

  const queryStartDate = queryDates[queryDates.length - 1];
  const queryEndDate = queryDates[0];

  let startTime = `${queryStartDate} 00:00:00`;
  let endTime = `${queryEndDate} 23:59:59`;

  if (!allDay) {
    if (
      queryStartDate === startDate.format(dateFormat) &&
      startHour !== defaultStartTime
    ) {
      startTime = `${queryStartDate} ${startHour}:00`;
    }

    if (
      queryEndDate === endDate.format(dateFormat) &&
      endHour !== defaultEndTime
    ) {
      endTime = `${queryEndDate} ${endHour}:00`;
    }
  }

  return { startTime, endTime };
}

function* resetState() {
  yield put(setOrdersByDate({}));
  yield put(setNextCursor(''));
  yield put(setHasMore(false));
}

function* toggleTransactionDetail(isMobile) {
  if (isMobile) {
    yield put(showTransactionDetailModal());
  } else if ($('#mk-col-left').hasClass('col-sm-12')) {
    $('#mk-col-left, #mk-col-right').toggleClass('col-sm-12 col-md-6 col-xs-6');
    setTimeout(() => {
      $('#mk-col-right').fadeIn(200);
      $('[data-toggle="popover"]').popover();
    }, 600);
    yield;
  }
}

function* initForm(outletIds, date) {
  const { startDate, endDate } = date;
  const allDay = moment(endDate).format('HH:mm') === '23:59';
  const startHour = !allDay
    ? moment(startDate).format('HH:mm')
    : DEFAULT_START_HOUR;
  const endHour = !allDay ? moment(endDate).format('HH:mm') : DEFAULT_END_HOUR;

  yield put(
    initialize('search', {
      outlet: outletIds,
      date: { startDate, endDate, startHour, endHour, allDay },
    })
  );
}

function* getOrderSummary(searchValues) {
  const { date, keyword, outletIds } = searchValues;

  if (keyword) {
    yield put(setOrderSummary({}));
    return;
  }

  const dateFormat = 'YYYY-MM-DD ';

  try {
    const response = yield call(apiSales.getOrderSummary, {
      startDate: formatDate(date, dateFormat).startTime,
      endDate: formatDate(date, dateFormat).endTime,
      outletIds,
    });

    yield put(setOrderSummary(response));
  } catch (error) {
    yield put(setOrderSummary({}));
  }
}

function* getDailyOrderSummaries(searchValues, queryDates) {
  const { keyword, outletIds, date } = searchValues;

  if (queryDates.length === 0 || keyword) {
    return;
  }

  const ordersByDate = {
    ...(yield select(
      (state: any) => state.reports.transactionsv2.ordersByDate
    )),
  };

  const formattedQueryDates = formatDateForDailySummaries(date, queryDates);

  try {
    const response = yield call(apiSales.getDailyOrderSummaries, {
      startDate: formattedQueryDates.startTime,
      endDate: formattedQueryDates.endTime,
      outletIds,
    });

    response
      .filter((orderSummary) => ordersByDate[orderSummary.date])
      .forEach((orderSummary) => {
        ordersByDate[orderSummary.date].totalCollected =
          orderSummary.totalCollected;
      });

    yield put(setOrdersByDate(ordersByDate));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

function* getOrders(searchValues) {
  const dailySummariesQueryDates: any[] = [];
  const ordersByDate = {
    ...(yield select(
      (state: any) => state.reports.transactionsv2.ordersByDate
    )),
  };

  const queryParam = yield call(getQueryParam, searchValues);
  const headers = yield call(getHeaders, searchValues);

  try {
    const ordersRes = yield call(
      apiOrderReporting.getOrders,
      queryParam,
      headers
    );

    const { orders } = ordersRes.data;

    orders.forEach((order) => {
      const date = order.created_at.split('T')[0];

      if (date in ordersByDate) {
        ordersByDate[date].orders.push(order);
      } else {
        dailySummariesQueryDates.push(date);

        ordersByDate[date] = {
          orders: new Array(order),
          totalCollected: 0,
        };
      }
    });

    yield put(setOrdersByDate(ordersByDate));

    const nextCursor = ordersRes.data.next_cursor;
    yield put(setNextCursor(nextCursor));
    yield put(setHasMore(!!nextCursor));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(showPageLoadingSpinner(false));
    yield put(action.hideProgressBar());
    yield call(getDailyOrderSummaries, searchValues, dailySummariesQueryDates);
  }
}

function* getTransactionDetail({ payload }: any) {
  const { id, isMobile } = payload;
  const { data: transactionDetail } = yield call(getOrderDetail, id);
  const sortedOrderItems = isMobile
    ? transactionDetail.order_items
    : sortOrderItems(transactionDetail.order_items);
  const sortedOrderRefunds = isMobile
    ? transactionDetail.order_refunds
    : transactionDetail.order_refunds.map((refund) => ({
        ...refund,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        order_items: sortOrderItems(refund.order_items),
      }));

  const { startDate, endDate } = yield call(getFilteredDateAsMoment);

  const updatedTransactionDetail = {
    ...transactionDetail,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    order_items: sortedOrderItems,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    order_refunds: sortedOrderRefunds,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    outlet_ids: getSelectedOutletIdFromLocalStorage(),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    start_date: startDate,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    end_date: endDate,
  };

  yield put(setOrderDetail(updatedTransactionDetail));
  yield put(
    initialize('resendEmailForm', { email: transactionDetail.customer_email })
  );

  const refundItems = extractForRefundBreakdown(transactionDetail);
  yield put(setRefundItems(refundItems));

  const refundItemsPromo = constructRefundItemsPromo(
    transactionDetail,
    refundItems
  );

  yield put(setRefundItemsPromo(refundItemsPromo));
  yield toggleTransactionDetail(isMobile);
}

export function* pageRequestFlow() {
  yield put(showPageLoadingSpinner(true));
  const resOutlets = yield call(getOutlets);
  yield put(actActionBar.setOutlets(resOutlets.data));

  const paymentMethods = yield call(getAllPaymentMethods);
  yield put(setPaymentMethods(paymentMethods));

  const outletById = Object.assign(
    {},
    ...resOutlets.data.map((outlet) => ({ [outlet.id]: outlet }))
  );
  yield put(setOutletById(outletById));

  const outletIds = getSelectedOutletIdFromLocalStorage();

  yield call(initForm, outletIds, yield call(getFilteredDateAsMoment));

  const searchValues = {
    // @ts-ignore
    date: yield select((state) => state.form.search.values.date),
    outletIds,
  };

  yield fork(getOrderSummary, searchValues);
  yield call(getOrders, searchValues);
}

export function* onSearch(request) {
  const { searchValues } = request.payload;
  const { outletIds } = searchValues;

  yield put(showPageLoadingSpinner(true));
  yield put(hideTransactionDetail());
  yield call(resetState);
  yield delay(300);

  yield fork(getOrderSummary, searchValues);
  yield call(getOrders, searchValues);
  setSelectedOutletIdToLocalStorage(outletIds);
}

export function* onReachBottom(request) {
  const { searchValues } = request.payload;

  yield put(action.showProgressBar());
  yield put(hideTransactionDetail());

  yield call(getOrders, searchValues);
}

export function* confirmSetQuantity(request) {
  const {
    payload: { refundItems },
  } = request;
  yield put(setRefundItems(refundItems));
  yield put(hideSetQuantityModal());
  yield put(initialize('setQuantity', { idx: null, quantity: null }));
}

export function* issueRefundFlow(request) {
  const { data } = request.payload;
  yield call(postRefundV2, data);

  // --- TODO: might need to switch to v2
  yield put(hideRefundBreakdownModal());
  yield put(hideTransactionDetailModal());
  yield put(hideTransactionDetail());
  //---

  yield call(resetState);
  yield put(showPageLoadingSpinner(true));
  yield delay(2000);
  yield put(pageRequest());
}

export default function* reportsTransactionsV2Saga() {
  yield takeLatest(REPORTS_TRANSACTIONS_PAGE_REQUEST, pageRequestFlow);
  yield takeLatest(REPORTS_TRANSACTIONS_ON_SEARCH, onSearch);
  yield takeLatest(REPORTS_TRANSACTIONS_ON_REACH_BOTTOM, onReachBottom);
  yield takeLatest(REPORTS_TRANSACTIONS_GET_ORDER_DETAIL, getTransactionDetail);
  yield takeLatest(REPORTS_TRANSACTIONS_ISSUE_REFUND_REQUEST, issueRefundFlow);
  yield takeLatest(
    REPORTS_TRANSACTIONS_CONFIRM_SET_QUANTITY,
    confirmSetQuantity
  );
}
